import { Suspense, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useSubmitPreferenceFeedback,
} from "@/notifications/api";
import { toast } from "sonner";
import { BellOff, Loader2, MapPin, Wrench } from "lucide-react";
import { useAllSnoozes, SnoozeListItem, useDeleteEquipmentSnooze, useDeleteLocationSnooze } from "@/hooks/useSnooze";
import { Spinner } from "@/components/ui/spinner";
import { format, formatDistanceToNow } from "date-fns";

export const Route = createFileRoute(
  "/_authenticated/settings/notifications"
)({
  component: NotificationPreferencesPage,
});

type SummaryFrequency = "daily" | "weekly" | "none";

const FEEDBACK_REASONS = [
  { value: "frequency", label: "Frequency" },
  { value: "not_interested", label: "Not Interested" },
  { value: "other", label: "Other" },
];

function frequencyFromPrefs(daily: boolean, weekly: boolean): SummaryFrequency {
  if (daily) return "daily";
  if (weekly) return "weekly";
  return "none";
}

function prefsFromFrequency(freq: SummaryFrequency) {
  return {
    daily_summary: freq === "daily",
    weekly_summary: freq === "weekly",
  };
}

type PendingChanges = {
  frequency?: SummaryFrequency;
  critical_alerts?: boolean;
};

type FeedbackItem = {
  field: string;
  previousValue: boolean;
  newValue: boolean;
};

// ---------------------------------------------------------------------------
// Snooze grouping
// ---------------------------------------------------------------------------

type SnoozeGroup = {
  locationId: number | null;
  locationName: string | null;
  locationSnooze: SnoozeListItem | null;
  equipmentSnoozes: SnoozeListItem[];
};

function groupSnoozes(snoozes: SnoozeListItem[]): SnoozeGroup[] {
  const locationSnoozes = snoozes.filter((s) => s.equipment === null);
  const equipmentSnoozes = snoozes.filter((s) => s.equipment !== null);

  const groupMap = new Map<number | null, SnoozeGroup>();

  for (const ls of locationSnoozes) {
    groupMap.set(ls.location, {
      locationId: ls.location,
      locationName: ls.location_name,
      locationSnooze: ls,
      equipmentSnoozes: [],
    });
  }

  for (const es of equipmentSnoozes) {
    const key = es.location ?? null;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        locationId: key,
        locationName: es.location_name ?? null,
        locationSnooze: null,
        equipmentSnoozes: [],
      });
    }
    groupMap.get(key)!.equipmentSnoozes.push(es);
  }

  return [...groupMap.values()].sort((a, b) => {
    if (a.locationId === null) return 1;
    if (b.locationId === null) return -1;
    return (a.locationName ?? "").localeCompare(b.locationName ?? "");
  });
}

// ---------------------------------------------------------------------------
// SnoozeRow
// ---------------------------------------------------------------------------

const SnoozeRow = ({
  snooze,
  indent = false,
}: {
  snooze: SnoozeListItem;
  indent?: boolean;
}) => {
  const deleteEquipment = useDeleteEquipmentSnooze();
  const deleteLocation = useDeleteLocationSnooze();

  const isEquipment = snooze.equipment !== null;
  const targetName = isEquipment ? snooze.equipment_name : snooze.location_name;
  const isPending = deleteEquipment.isPending || deleteLocation.isPending;

  const handleLift = () => {
    if (isEquipment && snooze.equipment !== null) {
      deleteEquipment.mutate(
        { equipmentId: snooze.equipment, snoozeId: snooze.id },
        {
          onSuccess: () => toast.success("Snooze lifted"),
          onError: () => toast.error("Failed to lift snooze"),
        }
      );
    } else if (snooze.location !== null) {
      deleteLocation.mutate(
        { locationId: snooze.location, snoozeId: snooze.id },
        {
          onSuccess: () => toast.success("Snooze lifted"),
          onError: () => toast.error("Failed to lift snooze"),
        }
      );
    }
  };

  const durationText = snooze.end
    ? `Until ${format(new Date(snooze.end), "MMM d, yyyy")} · ${formatDistanceToNow(new Date(snooze.end), { addSuffix: false })} remaining`
    : "Indefinitely";

  return (
    <div
      className={`flex items-center gap-3 py-2.5 pr-3 lg:pr-4 ${
        indent ? "pl-8 lg:pl-10" : "pl-3 lg:pl-4"
      }`}
    >
      <BellOff
        size={14}
        className={
          indent
            ? "text-muted-foreground shrink-0"
            : "text-snooze-purple shrink-0"
        }
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm truncate">
            {targetName ?? "Unknown"}
          </span>
          {isEquipment && (
            <Wrench size={11} className="text-muted-foreground shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{durationText}</p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleLift}
        disabled={isPending}
        className="shrink-0 h-7 text-xs"
      >
        {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Lift"}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActiveSnoozesList
// ---------------------------------------------------------------------------

const ActiveSnoozesList = () => {
  const { data: snoozes } = useAllSnoozes();

  if (!snoozes || snoozes.length === 0) {
    return (
      <div className="px-3 py-6 lg:px-4 text-center">
        <p className="text-sm text-muted-foreground">No active snoozes</p>
      </div>
    );
  }

  const groups = groupSnoozes(snoozes);

  return (
    <div className="divide-y">
      {groups.map((group) => (
        <div key={group.locationId ?? "unassigned"}>
          {/* Location header — either a snoozed location row or a label-only header */}
          {group.locationSnooze ? (
            <SnoozeRow snooze={group.locationSnooze} indent={false} />
          ) : group.locationName ? (
            <div className="flex items-center gap-2 px-3 py-2 lg:px-4">
              <MapPin size={13} className="text-muted-foreground shrink-0" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {group.locationName}
              </span>
            </div>
          ) : null}

          {/* Equipment snoozes nested under location */}
          {group.equipmentSnoozes.length > 0 && (
            <div className="divide-y bg-muted/30">
              {group.equipmentSnoozes.map((es) => (
                <SnoozeRow key={es.id} snooze={es} indent={true} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function NotificationPreferencesPage() {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const submitFeedback = useSubmitPreferenceFeedback();

  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});

  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");

  if (isLoading || !preferences) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentFrequency = frequencyFromPrefs(
    preferences.daily_summary,
    preferences.weekly_summary
  );

  const effectiveFrequency =
    pendingChanges.frequency !== undefined
      ? pendingChanges.frequency
      : currentFrequency;

  const effectiveCriticalAlerts =
    pendingChanges.critical_alerts !== undefined
      ? pendingChanges.critical_alerts
      : preferences.critical_alerts;

  const isDirty = Object.keys(pendingChanges).length > 0;

  const buildDiff = (): {
    update: Record<string, boolean>;
    feedback: FeedbackItem[];
  } => {
    const update: Record<string, boolean> = {};
    const feedback: FeedbackItem[] = [];

    if (
      pendingChanges.frequency !== undefined &&
      pendingChanges.frequency !== currentFrequency
    ) {
      const freqUpdate = prefsFromFrequency(pendingChanges.frequency);
      Object.assign(update, freqUpdate);

      if (freqUpdate.daily_summary !== preferences.daily_summary) {
        feedback.push({
          field: "daily_summary",
          previousValue: preferences.daily_summary,
          newValue: freqUpdate.daily_summary,
        });
      }
      if (freqUpdate.weekly_summary !== preferences.weekly_summary) {
        feedback.push({
          field: "weekly_summary",
          previousValue: preferences.weekly_summary,
          newValue: freqUpdate.weekly_summary,
        });
      }
    }

    if (
      pendingChanges.critical_alerts !== undefined &&
      pendingChanges.critical_alerts !== preferences.critical_alerts
    ) {
      update.critical_alerts = pendingChanges.critical_alerts;
      feedback.push({
        field: "critical_alerts",
        previousValue: preferences.critical_alerts,
        newValue: pendingChanges.critical_alerts,
      });
    }

    return { update, feedback };
  };

  const handleSave = () => {
    const { update, feedback } = buildDiff();
    if (Object.keys(update).length === 0) return;

    updatePreferences.mutate(update, {
      onSuccess: () => {
        toast.success("Preferences saved");
        setPendingChanges({});

        if (feedback.length > 0) {
          setFeedbackItems(feedback);
          setFeedbackReason("");
          setFeedbackComment("");
          setFeedbackOpen(true);
        }
      },
      onError: () => toast.error("Failed to save preferences"),
    });
  };

  const handleFeedbackSubmit = () => {
    if (feedbackItems.length === 0) return;

    const reason = feedbackReason;
    const comment = feedbackComment.trim() || undefined;

    for (const item of feedbackItems) {
      submitFeedback.mutate({
        preference_field: item.field,
        previous_value: item.previousValue,
        new_value: item.newValue,
        reason,
        comment,
      });
    }

    closeFeedback();
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackItems([]);
    setFeedbackReason("");
    setFeedbackComment("");
  };

  const frequencyOptions: {
    value: SummaryFrequency;
    label: string;
    description: string;
  }[] = [
    {
      value: "daily",
      label: "Daily",
      description: "Receive a health summary every morning",
    },
    {
      value: "weekly",
      label: "Weekly",
      description: "Receive a health summary every Monday",
    },
    {
      value: "none",
      label: "None",
      description: "Don't receive health summaries",
    },
  ];

  const fieldLabels: Record<string, string> = {
    daily_summary: "Daily Summary",
    weekly_summary: "Weekly Summary",
    critical_alerts: "Critical Issue Alerts",
  };

  return (
    <>
      <div className="space-y-4">
        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Notification Settings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage how and when you receive notifications from Copilot.
          </p>
        </div>

        {/* Summary Frequency */}
        <Card>
          <div className="p-3 lg:p-4">
            <h2 className="font-semibold text-lg">Summary Frequency</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Choose how often you'd like to receive org-wide health summaries.
            </p>
          </div>
          <div className="divide-y">
            {frequencyOptions.map(({ value, label, description }) => (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setPendingChanges((prev) => ({ ...prev, frequency: value }))
                }
                className={`flex w-full items-center gap-3 px-3 py-4 text-left transition-colors lg:px-4 ${
                  effectiveFrequency === value
                    ? "bg-accent"
                    : "hover:bg-accent/50"
                }`}
              >
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    effectiveFrequency === value
                      ? "border-primary bg-primary"
                      : "border-muted-foreground"
                  }`}
                >
                  {effectiveFrequency === value && (
                    <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Critical Alerts */}
        <Card>
          <div className="flex items-center justify-between gap-4 p-3 lg:p-4">
            <div className="min-w-0">
              <h2 className="font-semibold text-lg">Critical Issue Alerts</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Receive email notifications for critical issues. You will appear
                as a recipient option when staff send issue alert notifications.
              </p>
            </div>
            <Switch
              checked={effectiveCriticalAlerts}
              onCheckedChange={(value) =>
                setPendingChanges((prev) => ({
                  ...prev,
                  critical_alerts: value,
                }))
              }
            />
          </div>
        </Card>

        {/* Snoozed Notifications */}
        <Card>
          <div className="p-3 lg:p-4">
            <h2 className="font-semibold text-lg">Snoozed Notifications</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Locations and equipment you've snoozed. You won't receive
              notifications for these items.
            </p>
          </div>
          <Suspense
            fallback={
              <div className="px-3 py-6 lg:px-4 flex justify-center">
                <Spinner />
              </div>
            }
          >
            <ActiveSnoozesList />
          </Suspense>
        </Card>

        {/* Save button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!isDirty || updatePreferences.isPending}
          >
            {updatePreferences.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      {/* Feedback Modal */}
      <Dialog
        open={feedbackOpen}
        onOpenChange={(open) => {
          if (!open) closeFeedback();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Help us improve</DialogTitle>
            <DialogDescription>
              You updated your notification preferences. Mind sharing why?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-md border divide-y">
              {feedbackItems.map((item) => (
                <div
                  key={item.field}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <span className="font-medium">
                    {fieldLabels[item.field] ?? item.field}
                  </span>
                  <span className="text-muted-foreground">
                    {item.previousValue ? "on" : "off"} →{" "}
                    {item.newValue ? "on" : "off"}
                  </span>
                </div>
              ))}
            </div>

            <Select value={feedbackReason} onValueChange={setFeedbackReason}>
              <SelectTrigger>
                <SelectValue placeholder="Reason (optional)" />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_REASONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Textarea
              placeholder="Any additional comments? (optional)"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeFeedback}>
              Skip
            </Button>
            <Button onClick={handleFeedbackSubmit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}